import React, { Suspense, lazy, useEffect } from "react";
import RouteLoader from "./components/RouteLoader";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate, useSearchParams, useParams } from "react-router-dom";
import { pathLang, stripLangPrefix, needsLangRedirect } from "./utils/langRoute";
import "@/App.css";
import { LangProvider, useLang } from "./LangContext";
import LangSupabaseSync from "./components/LangSupabaseSync";
import GoogleSiteTranslator from "./components/GoogleSiteTranslator";
import { AuthProvider } from "./AuthContext";
import { SHOW_PAID_SERVICES } from "./config/featureFlags";
import { SupabaseAuthProvider } from "./SupabaseAuthContext";
import ProtectedRoute from "./components/auth/ProtectedRoute";
import GovernanceGate from "./components/governance/GovernanceGate";
import Header from "./components/layout/NavbarTerritorialeTop";
import Footer from "./components/layout/FooterTerritorialeCompleto";
import AmbasciatoreFab from "./components/layout/AmbasciatoreFab";
import ChatWidget from "./components/ChatWidget";
import ChatLauncher from "./components/ChatLauncher";
import OAuthHashHandler from "./components/OAuthHashHandler";
import ScrollToTop from "./components/ScrollToTop";
import { ThemeLayoutProvider, useThemeLayout } from "./ThemeLayoutContext";
import { InterMappaProvider } from "./context/InterMappaContext";
import { CiceroneProvider } from "./context/CiceroneContext";
import { isAppPath, isEmbedQuery } from "./utils/appMode";
import MapQuickAccess from "./components/map/MapQuickAccess";
import AppErrorBoundary from "./components/AppErrorBoundary";
import { AnalyticsRouteListener } from "./utils/analytics";
import LaunchSeoMeta from "./components/LaunchSeoMeta";
import "./google-translate.css";

/**
 * Manutenzione sito — opt-in ESPLICITO.
 *
 * Prima era opt-out: senza la variabile (build su un'altra macchina, CI, clone
 * pulito senza `frontend/.env.local`, che è gitignored) il sito finiva INTERAMENTE
 * in manutenzione. Ora `true` è l'unico valore che attiva la manutenzione:
 * assente / qualsiasi altro valore = sito ONLINE.
 */
const isMaintenanceMode = process.env.REACT_APP_MAINTENANCE_MODE === "true";

function isAdminAccessPath(pathname) {
  return pathname === "/admin" || pathname.startsWith("/admin/");
}

function MaintenanceLanding() {
  return (
    <div
      className="relative flex min-h-screen flex-col items-center justify-center overflow-hidden bg-[#020617] px-6 py-16 text-slate-300"
      data-testid="maintenance-landing"
    >
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_top,_rgba(180,83,9,0.12),_transparent_55%)]" aria-hidden />
      <div className="pointer-events-none absolute -left-24 top-1/4 h-64 w-64 rounded-full bg-amber-900/20 blur-3xl" aria-hidden />
      <div className="pointer-events-none absolute -right-24 bottom-1/4 h-72 w-72 rounded-full bg-slate-700/20 blur-3xl" aria-hidden />

      <div className="relative z-10 w-full max-w-xl text-center">
        <p className="font-mono text-[10px] uppercase tracking-[0.32em] text-amber-500/90 mb-6">
          6amico · rete territoriale
        </p>
        <h1 className="font-display text-4xl md:text-5xl text-[#f8fafc] leading-tight mb-6">
          6AMICO — Piattaforma in Manutenzione
        </h1>
        <p className="text-base md:text-lg leading-relaxed text-slate-400 max-w-lg mx-auto">
          Stiamo completando l&apos;esperienza digitale del territorio. La{" "}
          <span className="text-amber-200/90">Piazza Social</span> e le funzioni avanzate del network
          saranno online prossimamente.
        </p>
        <p className="mt-8 text-sm text-slate-500">
          Grazie per la pazienza — torna a trovarci tra poco.
        </p>
          <div className="mt-10 inline-flex items-center gap-2 rounded-full border border-amber-900/40 bg-slate-900/50 px-5 py-2 font-mono text-[10px] uppercase tracking-widest text-slate-500">
          <span className="h-2 w-2 animate-pulse rounded-full bg-amber-500" aria-hidden />
          Manutenzione in corso
        </div>
        <p className="mt-8">
          <a
            href="/admin"
            className="text-xs text-slate-500 underline-offset-2 hover:text-amber-300 hover:underline"
          >
            Accesso operatori · Super Admin
          </a>
        </p>
      </div>
    </div>
  );
}

const Home = lazy(() => import("./pages/Home"));
const HomeCampli = lazy(() => import("./pages/HomeCampli"));
const NodoZeroLuogo = lazy(() => import("./pages/NodoZeroLuogo"));
const VillageDetail = lazy(() => import("./pages/VillageDetail"));
const BorgoToComuneRedirect = lazy(() => import("./pages/BorgoToComuneRedirect"));
const BorgoLuogoResolver = lazy(() => import("./pages/BorgoLuogoResolver"));
const QRPage = lazy(() => import("./pages/QRPage"));
const Piazze = lazy(() => import("./pages/Piazze"));
const PostView = lazy(() => import("./pages/PostView"));
const PiazzaImmersiva = lazy(() => import("./pages/PiazzaImmersiva"));
const Profilo = lazy(() => import("./pages/Profilo"));
const Vetrina = lazy(() => import("./pages/Vetrina"));
const Gemellaggi = lazy(() => import("./pages/Gemellaggi"));
const ComuniAmici = lazy(() => import("./pages/ComuniAmici"));
const RelazioniTerritorio = lazy(() => import("./pages/RelazioniTerritorio"));
const FrazioneDetail = lazy(() => import("./pages/FrazioneDetail"));
const Global = lazy(() => import("./pages/Global"));
const AdminPanel = lazy(() => import("./pages/AdminPanel"));
const GeneratoreAttestati = lazy(() => import("./pages/admin/GeneratoreAttestati"));
const ApprovazioneIscrizioni = lazy(() => import("./pages/admin/ApprovazioneIscrizioni"));
const DelegazioniAdmin = lazy(() => import("./pages/admin/DelegazioniAdmin"));
const Delegation = lazy(() => import("./pages/Delegation"));
const DelegationEspace = lazy(() => import("./pages/DelegationEspace"));
const ComuniIndex = lazy(() => import("./pages/ComuniIndex"));
const ComuneDetail = lazy(() => import("./pages/ComuneDetail"));
const ComuneImpronta = lazy(() => import("./pages/ComuneImpronta"));
const SicurezzaTracciatiRoute = lazy(() => import("./pages/SicurezzaTracciatiRoute"));
const RichiediAccesso = lazy(() => import("./pages/RichiediAccesso"));
const AccountLogin = lazy(() => import("./pages/AccountLogin"));
const AccountDashboard = lazy(() => import("./pages/AccountDashboard"));
const EventiIndex = lazy(() => import("./pages/EventiIndex"));
const EventoDetail = lazy(() => import("./pages/EventoDetail"));
const Weekend = lazy(() => import("./pages/Weekend"));
const Prodotti = lazy(() => import("./pages/Prodotti"));
const ProductDetail = lazy(() => import("./pages/ProductDetail"));
const BusinessDetail = lazy(() => import("./pages/BusinessDetail"));
const CookiePolicy = lazy(() => import("./pages/CookiePolicy"));
const Termini = lazy(() => import("./pages/Termini"));
const Privacy = lazy(() => import("./pages/Privacy"));
const RegolePiazza = lazy(() => import("./pages/RegolePiazza"));
const CreatorPassBorghi = lazy(() => import("./pages/CreatorPassBorghi"));
const ChiSiamo = lazy(() => import("./pages/ChiSiamo"));
const ArticoloFamigliaFumo = lazy(() => import("./pages/ArticoloFamigliaFumo"));
const Contatti = lazy(() => import("./pages/Contatti"));
const MonumentBySlug = lazy(() => import("./pages/MonumentBySlug"));
const NotFound = lazy(() => import("./pages/NotFound"));
const Origini = lazy(() => import("./pages/Origini"));
const PerComuni = lazy(() => import("./pages/PerComuni"));
const LegacyTerritorialQr = lazy(() => import("./pages/LegacyTerritorialQr"));
const CampliLegacySlug = lazy(() => import("./pages/CampliLegacySlug"));
const ArchiveCollectionView = lazy(() => import("./pages/ArchiveCollectionView"));
const Registrati = lazy(() => import("./pages/Registrati"));
const Collabora = lazy(() => import("./pages/Collabora"));
const LoginUnificato = lazy(() => import("./pages/LoginUnificato"));
const CensisciLuogo = lazy(() => import("./pages/CensisciLuogo"));
const MappaDelMondo = lazy(() => import("./pages/MappaDelMondo"));
const ArchivioPodcast = lazy(() => import("./pages/ArchivioPodcast"));
const ParchiNaturaIndex = lazy(() => import("./pages/ParchiNaturaIndex"));
const ParcoNatura = lazy(() => import("./pages/ParcoNatura"));
const MareCosta = lazy(() => import("./pages/MareCosta"));
const TourEscursioni = lazy(() => import("./pages/TourEscursioni"));
const TermeBenessere = lazy(() => import("./pages/TermeBenessere"));
const TurismoNautico = lazy(() => import("./pages/TurismoNautico"));
const TourEscursioniDetail = lazy(() => import("./pages/TourEscursioniDetail"));
const FiereConvegni = lazy(() => import("./pages/FiereConvegni"));
const Spiritualita = lazy(() => import("./pages/Spiritualita"));
const MappaTerritorio = lazy(() => import("./pages/MappaTerritorio"));
const MappaInterattiva = lazy(() => import("./pages/MappaInterattiva"));
const MappaGlobale = lazy(() => import("./pages/MappaGlobale"));
const EsploraLaMappa = lazy(() => import("./pages/EsploraLaMappa"));
const Stampa = lazy(() => import("./pages/Stampa"));
const UfficioStampa = lazy(() => import("./pages/UfficioStampa"));
const ArchivioEventi = lazy(() => import("./pages/ArchivioEventi"));
const Mondo = lazy(() => import("./pages/Mondo"));
const ForgotPassword = lazy(() => import("./pages/ForgotPassword"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const SuggerisciEvento = lazy(() => import("./pages/SuggerisciEvento"));
const ComeFunziona = lazy(() => import("./pages/ComeFunziona"));
const CapsuleDelTempo = lazy(() => import("./pages/CapsuleDelTempo"));
const PuntiMerito = lazy(() => import("./pages/PuntiMerito"));
const SentinellaSismica = lazy(() => import("./pages/SentinellaSismica"));
const MonumentDetail = lazy(() => import("./pages/MonumentDetail"));
const UnlockCampovalano = lazy(() => import("./pages/UnlockCampovalano"));
const CittaDetail = lazy(() => import("./pages/CittaDetail"));
const UnescoPassportGlobal = lazy(() => import("./pages/UnescoPassport"));
const UnescoPassportPage = lazy(() => import("./components/unesco-passport/UnescoPassportPage"));
const UnescoImpattoDashboard = lazy(() => import("./pages/UnescoImpattoDashboard"));
const RottaAdriatica = lazy(() => import("./pages/RottaAdriatica"));
const PortoDetail = lazy(() => import("./pages/PortoDetail"));
const RottaNautica = lazy(() => import("./pages/RottaNautica"));
const Parrocchie = lazy(() => import("./pages/Parrocchie"));
const ParrocchiaDetail = lazy(() => import("./pages/ParrocchiaDetail"));
const ChurchAdmin = lazy(() => import("./pages/ChurchAdmin"));
const ModerazioneFotoPage = lazy(() => import("./pages/ModerazioneFotoPage"));
const LeMieRichieste = lazy(() => import("./pages/LeMieRichieste"));
const MioSpazio = lazy(() => import("./pages/MioSpazio"));
const ComuneWorkspace = lazy(() => import("./pages/ComuneWorkspace"));
const WorldCityWorkspace = lazy(() => import("./pages/WorldCityWorkspace"));
const Agora = lazy(() => import("./pages/Agora"));
const StazioneMeteoEmotiva = lazy(() => import("./pages/StazioneMeteoEmotiva"));
const Emergenza = lazy(() => import("./pages/Emergenza"));
const CittadinanzaAttiva = lazy(() => import("./pages/CittadinanzaAttiva"));
const HostOnboarding = lazy(() => import("./pages/HostOnboarding"));
const HostDashboard = lazy(() => import("./pages/HostDashboard"));
const BookingResult = lazy(() => import("./pages/BookingResult"));
const AccountBookings = lazy(() => import("./pages/AccountBookings"));
const Manifesto = lazy(() => import("./pages/Manifesto"));
const ProLocoIndex = lazy(() => import("./pages/ProLocoIndex"));
const ProLocoServizioCivile = lazy(() => import("./pages/ProLocoServizioCivile"));
const FlipbookViewer = lazy(() => import("./pages/FlipbookViewer"));
const VideoViewer = lazy(() => import("./pages/VideoViewer"));
const Universita = lazy(() => import("./pages/Universita"));
const Ambasciatori = lazy(() => import("./pages/Ambasciatori"));
const RetePiazze = lazy(() => import("./pages/RetePiazze"));
const UfficioPostale = lazy(() => import("./pages/UfficioPostale"));
const Aderisci = lazy(() => import("./pages/Aderisci"));
const Sanita = lazy(() => import("./pages/Sanita"));
const RegistroInnovazione = lazy(() => import("./pages/RegistroInnovazione"));
const GovernanceDashboardPage = lazy(() => import("./pages/GovernanceDashboardPage"));
const MayorsCouncilPage = lazy(() => import("./pages/MayorsCouncilPage"));
const NetworkGovernance = lazy(() => import("./pages/NetworkGovernance"));
const CentraleComando = lazy(() => import("./pages/CentraleComando"));
const Generazione6Amico = lazy(() => import("./pages/Generazione6Amico"));
const HubRegionale = lazy(() => import("./pages/HubRegionale"));
const Opportunita1Euro = lazy(() => import("./pages/Opportunita1Euro"));
const ComuneCase1Euro = lazy(() => import("./pages/ComuneCase1Euro"));
const ComuniImport = lazy(() => import("./pages/admin/ComuniImport"));
const EccellenzeIndex = lazy(() => import("./pages/EccellenzeIndex"));
const EccellenzaDetail = lazy(() => import("./pages/EccellenzaDetail"));
const Cammini = lazy(() => import("./pages/Cammini"));
const CamminoDetail = lazy(() => import("./pages/CamminoDetail"));
const EmbedFrame = lazy(() => import("./pages/EmbedFrame"));
const SegnalaDegradoRoute = lazy(() => import("./pages/SegnalaDegradoRoute"));
const MissioniOperative = lazy(() => import("./pages/MissioniOperative"));
const CondizioniTutela = lazy(() => import("./pages/CondizioniTutela"));
const GuidaIndex = lazy(() => import("./pages/GuidaIndex"));
const GuidaBenvenuto = lazy(() => import("./pages/GuidaBenvenuto"));
const StoriaProtocollo = lazy(() => import("./pages/StoriaProtocollo"));
const ProtocolloVetrina = lazy(() => import("./pages/ProtocolloVetrina"));
const Impronta = lazy(() => import("./pages/Impronta"));
const AppEntry = lazy(() => import("./pages/AppEntry"));
const AppHome = lazy(() => import("./pages/AppHome"));
const VetrinaWebview = lazy(() => import("./pages/VetrinaWebview"));
const MobileToAppRedirect = lazy(() =>
  import("./pages/MobileRedirect").then((m) => ({ default: m.MobileToAppRedirect })),
);
const MobileComuneRedirect = lazy(() =>
  import("./pages/MobileRedirect").then((m) => ({ default: m.MobileComuneRedirect })),
);
const AppComuneRedirect = lazy(() =>
  import("./pages/MobileRedirect").then((m) => ({ default: m.AppComuneRedirect })),
);

/** Su porta 3000 le richieste /api/* vanno al backend (8001), non al router React. */
function ApiBackendRedirect() {
  useEffect(() => {
    const base = (process.env.REACT_APP_BACKEND_URL || "http://127.0.0.1:8001").replace(/\/$/, "");
    window.location.replace(`${base}${window.location.pathname}${window.location.search}`);
  }, []);
  return (
    <div className="min-h-[40vh] flex items-center justify-center text-ink2 text-sm" role="status">
      Collegamento all&apos;API backend…
    </div>
  );
}

function ScrollToHash() {
  const { hash } = useLocation();
  useEffect(() => {
    if (!hash) return;
    const id = hash.replace(/^#/, "");
    if (!id) return;
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  }, [hash]);
  return null;
}

/** Alias storico /mappa-globale → mappa canonica, preservando la query. */
function MappaGlobaleRedirect() {
  const q = typeof window !== "undefined" ? window.location.search || "" : "";
  return <Navigate to={`/esplora-la-mappa${q}`} replace />;
}

/** /g|/fr|/ct short share → canonical scheda (nginx 301 also handles full page loads). */
function ShortShareRedirect({ toPrefix }) {
  const { slug } = useParams();
  const clean = String(slug || "").split(/\s+/)[0];
  const q = typeof window !== "undefined" ? window.location.search || "" : "";
  return <Navigate to={`${toPrefix}/${clean}${q}`} replace />;
}

/**
 * BLOCCO C — sincronizza lingua ↔ URL.
 * /en/ /fr/ /es/ = lingue vere indicizzabili; l'URL vince sulla preferenza
 * salvata, e la navigazione interna mantiene il prefisso scelto.
 */
function LangPrefixSync() {
  const { lang, setLang } = useLang();
  const { pathname, search } = useLocation();
  const navigate = useNavigate();
  const seg = pathLang(pathname);

  useEffect(() => {
    if (seg && seg !== lang) setLang(seg);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seg]);

  useEffect(() => {
    if (!seg && needsLangRedirect(pathname, lang)) {
      navigate(`/${lang}${pathname}${search || ""}`, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname, search, seg, lang]);

  return null;
}

function AppRouter() {
  const { lang, langEpoch } = useLang();
  const { pathname } = useLocation();
  // Le route restano definite senza prefisso: /en/comune/x → match /comune/x.
  const displayPath = stripLangPrefix(pathname);
  return (
    <>
      <OAuthHashHandler/>
      <ScrollToTop/>
      <LangPrefixSync/>
      <Suspense fallback={<RouteLoader/>}>
      <Routes key={`i18n-${lang}-${langEpoch}`} location={displayPath}>
        {/* Legacy WordPress → pannello 6amico (evita confusione con vecchio wp-admin) */}
        <Route path="/wp-admin" element={<Navigate to="/admin" replace/>}/>
        <Route path="/wp-admin/*" element={<Navigate to="/admin" replace/>}/>
        <Route path="/wp-login.php" element={<Navigate to="/admin" replace/>}/>
        <Route path="/wp-content/*" element={<Navigate to="/" replace/>}/>
        <Route path="/wp-includes/*" element={<Navigate to="/" replace/>}/>

        <Route path="/" element={<Home/>}/>
      <Route path="/campli" element={<HomeCampli/>}/>
      <Route path="/campli/nodo/:nodeSlug" element={<NodoZeroLuogo/>}/>
      <Route path="/campli/campli-home" element={<Navigate to="/comune/campli" replace/>}/>
      <Route path="/campli/home" element={<Navigate to="/comune/campli" replace/>}/>
      <Route path="/campli/:slug" element={<CampliLegacySlug/>}/>
      <Route path="/crognaleto/crognaleto-home" element={<LegacyTerritorialQr village="crognaleto" slug="crognaleto-home"/>}/>
      <Route path="/assergi" element={<Navigate to="/comune/assergi" replace/>}/>
      <Route path="/paganica" element={<Navigate to="/comune/paganica" replace/>}/>
      <Route path="/campo-imperatore" element={<Navigate to="/comune/campo-imperatore" replace/>}/>
      <Route path="/assisi" element={<Navigate to="/comune/assisi" replace/>}/>
      <Route path="/comune/assisi-san-francesco" element={<Navigate to="/comune/assisi" replace/>}/>
      <Route path="/comune/assisi-storia-origini" element={<Navigate to="/comune/assisi#tab-storia" replace/>}/>
      <Route path="/rocca-calascio" element={<Navigate to="/frazione/calascio__rocca-calascio" replace/>}/>
      <Route path="/comune/rocca-calascio" element={<Navigate to="/comune/calascio" replace/>}/>
      <Route path="/frazione/crognaleto" element={<Navigate to="/comune/crognaleto" replace/>}/>
      <Route path="/frazione/nerito" element={<Navigate to="/frazione/crognaleto__nerito" replace/>}/>
      {/* Rename 2026-10: slug piatti → canonico comune__frazione (301 anche lato nginx) */}
      <Route path="/frazione/crognaleto-paese" element={<Navigate to="/frazione/crognaleto__crognaleto-paese" replace/>}/>
      <Route path="/frazione/cesena" element={<Navigate to="/frazione/campli__cesena" replace/>}/>
      <Route path="/frazione/paduli" element={<Navigate to="/frazione/campli__paduli" replace/>}/>
      <Route path="/frazione/paterno" element={<Navigate to="/frazione/campli__paterno" replace/>}/>
      <Route path="/frazione/cervaro" element={<Navigate to="/frazione/crognaleto__cervaro" replace/>}/>
      <Route path="/frazione/montone" element={<Navigate to="/frazione/mosciano-sant-angelo__montone" replace/>}/>
      <Route path="/frazione/rocca-calascio" element={<Navigate to="/frazione/calascio__rocca-calascio" replace/>}/>
      <Route path="/torre-del-cerrano" element={<Navigate to="/frazione/teramo__torre-del-cerrano" replace/>}/>
      <Route path="/frazione/torre-del-cerrano" element={<Navigate to="/frazione/teramo__torre-del-cerrano" replace/>}/>
      <Route path="/frazione/pineto__torre-del-cerrano" element={<Navigate to="/frazione/teramo__torre-del-cerrano" replace/>}/>
      <Route path="/frazione/assisi__torre-del-cerrano" element={<Navigate to="/frazione/teramo__torre-del-cerrano" replace/>}/>
      <Route path="/frazione/assergi__torre-del-cerrano" element={<Navigate to="/frazione/teramo__torre-del-cerrano" replace/>}/>
      <Route path="/mosciano" element={<Navigate to="/comune/mosciano-sant-angelo" replace/>}/>
      <Route path="/mosciano.html" element={<Navigate to="/comune/mosciano-sant-angelo" replace/>}/>
      <Route path="/mosciano/home" element={<Navigate to="/comune/mosciano-sant-angelo" replace/>}/>
      <Route path="/mosciano/mosciano-home" element={<Navigate to="/comune/mosciano-sant-angelo" replace/>}/>
      <Route
        path="/aggiungimiaipreferiti.html"
        element={<Navigate to="/comune/mosciano-sant-angelo?legacy=preferiti" replace/>}
      />
      <Route path="/mosciano/:slug" element={<LegacyTerritorialQr village="mosciano"/>}/>
      <Route path="/mosciano-sant-angelo/:slug" element={<LegacyTerritorialQr village="mosciano-sant-angelo"/>}/>
      <Route path="/crognaleto/:slug" element={<LegacyTerritorialQr village="crognaleto"/>}/>
      <Route path="/home-campli" element={<Navigate to="/campli" replace/>}/>
      <Route path="/borgo/:village/:luogo" element={<BorgoLuogoResolver/>}/>
      <Route path="/borgo/:slug" element={<BorgoToComuneRedirect/>}/>
      <Route path="/frazione/:slug" element={<FrazioneDetail/>}/>
      <Route path="/comuni" element={<ComuniIndex/>}/>
      <Route path="/hub-regionale" element={<HubRegionale/>}/>
      <Route path="/rete-abruzzo" element={<HubRegionale/>}/>
      <Route path="/opportunita-1-euro" element={<Opportunita1Euro/>}/>
      <Route path="/admin/comuni-import" element={<ComuniImport/>}/>
      <Route path="/dashboard-amministratore/comuni" element={<ComuniImport/>}/>
      <Route path="/mappa-del-mondo" element={<MappaDelMondo/>}/>
      <Route path="/italia" element={<Navigate to="/mappa-del-mondo" replace/>}/>
      <Route path="/archivio-podcast" element={<ArchivioPodcast/>}/>
      <Route path="/podcast" element={<Navigate to="/archivio-podcast" replace/>}/>
      <Route path="/parchi" element={<ParchiNaturaIndex/>}/>
      <Route path="/parco/:slug" element={<ParcoNatura/>}/>
      <Route path="/mare-costa" element={<MareCosta/>}/>
      <Route path="/stabilimenti-balneari" element={<Navigate to="/mare-costa" replace/>}/>
      <Route path="/escursioni" element={<Navigate to="/tour-escursioni" replace/>}/>
      <Route path="/terme-benessere" element={<TermeBenessere/>}/>
      <Route path="/salute-terme-e-benessere" element={<Navigate to="/terme-benessere" replace/>}/>
      <Route path="/turismo-nautico" element={<TurismoNautico/>}/>
      <Route path="/nautica" element={<Navigate to="/turismo-nautico" replace/>}/>
      <Route path="/tour-escursioni" element={<TourEscursioni/>}/>
      <Route path="/tour-escursioni/:slug" element={<TourEscursioniDetail/>}/>
      <Route path="/fiere-convegni" element={<FiereConvegni/>}/>
      <Route path="/fiere" element={<Navigate to="/fiere-convegni" replace/>}/>
      <Route path="/spiritualita" element={<Spiritualita/>}/>
      <Route path="/cosa-fare/spiritualita" element={<Navigate to="/spiritualita" replace/>}/>
      <Route path="/mondo" element={<Mondo/>}/>
      <Route path="/citta/:slug" element={<CittaDetail/>}/>
      <Route path="/passport" element={<UnescoPassportGlobal/>}/>
      <Route path="/passaporto" element={<UnescoPassportPage/>}/>
      <Route path="/passaporto-unesco" element={<UnescoPassportPage/>}/>
      <Route path="/profilo/passaporto" element={<UnescoPassportPage/>}/>
      <Route path="/rotta-adriatica" element={<RottaAdriatica/>}/>
      <Route path="/porto/:id" element={<PortoDetail/>}/>
      <Route path="/rotta-nautica" element={<RottaNautica/>}/>
      <Route path="/parrocchie" element={<Parrocchie/>}/>
      <Route path="/pro-loco" element={<ProLocoIndex/>}/>
      <Route path="/pro-loco-servizio-civile" element={<ProLocoServizioCivile/>}/>
      <Route path="/libro/:id" element={<FlipbookViewer/>}/>
      <Route path="/video" element={<VideoViewer/>}/>
      <Route path="/parrocchia/:slug" element={<ParrocchiaDetail/>}/>
      <Route path="/admin/church" element={<ChurchAdmin/>}/>
      <Route path="/moderazione-foto" element={<ModerazioneFotoPage/>}/>
      <Route path="/le-mie-richieste" element={<LeMieRichieste/>}/>
      <Route path="/il-mio-spazio" element={<MioSpazio/>}/>
      <Route path="/admin/comune/:slug" element={<ComuneWorkspace/>}/>
      <Route path="/admin/citta/:slug" element={<WorldCityWorkspace/>}/>
      <Route path="/comune/mosciano-sant-angelo/famiglia-fumo-caffe-grande-italia-usa" element={<ArticoloFamigliaFumo/>}/>
      <Route path="/comune/:slug/case-1-euro" element={<ComuneCase1Euro/>}/>
      <Route path="/comune/:slug/impronta" element={<ComuneImpronta/>}/>
      <Route path="/comune/:slug" element={<ComuneDetail/>}/>
      <Route path="/g/:slug" element={<ShortShareRedirect toPrefix="/comune" />}/>
      <Route path="/fr/:slug" element={<ShortShareRedirect toPrefix="/frazione" />}/>
      <Route path="/ct/:slug" element={<ShortShareRedirect toPrefix="/citta" />}/>
      {/* QR storici stampati: /it/comuni/campli → /comune/campli */}
      <Route path="/it/comuni/:slug" element={<ShortShareRedirect toPrefix="/comune" />}/>
      <Route path="/it/comune/:slug" element={<ShortShareRedirect toPrefix="/comune" />}/>
      <Route path="/impronta" element={<Impronta/>}/>
      <Route path="/app" element={<AppEntry/>}/>
      <Route path="/app/comune/:slug" element={<AppComuneRedirect/>}/>
      <Route path="/app/webview" element={<VetrinaWebview/>}/>
      <Route path="/mobile" element={<Navigate to="/app" replace/>}/>
      <Route path="/mobile/comune/:slug" element={<MobileComuneRedirect/>}/>
      <Route path="/mobile/*" element={<MobileToAppRedirect/>}/>
      <Route path="/comune/:slug/segnala-degrado" element={<SegnalaDegradoRoute/>}/>
      <Route path="/comune/:slug/sicurezza-tracciati" element={<SicurezzaTracciatiRoute/>}/>
      <Route path="/missioni-operative" element={<MissioniOperative/>}/>
      <Route path="/servizio-civile/operativo" element={<MissioniOperative/>}/>
      <Route path="/condizioni-tutela" element={<CondizioniTutela/>}/>
      <Route path="/qr" element={<QRPage/>}/>
      <Route path="/piazze" element={<PiazzaImmersiva/>}/>
      <Route path="/rete-piazze" element={<RetePiazze/>}/>
      <Route path="/ufficio-postale" element={<UfficioPostale/>}/>
      <Route path="/piazze-classica" element={<Piazze/>}/>
      <Route path="/profilo" element={<Profilo/>}/>
      <Route path="/post/:postId" element={<PostView/>}/>
      <Route path="/piazze-immersiva" element={<PiazzaImmersiva/>}/>
      <Route path="/vetrina" element={<Vetrina/>}/>
      <Route path="/vetrina/:id" element={<BusinessDetail/>}/>
      <Route path="/gemellaggi" element={<Gemellaggi/>}/>
      <Route path="/comuni-amici" element={<ComuniAmici/>}/>
      <Route path="/relazioni-territorio" element={<RelazioniTerritorio/>}/>
      <Route path="/admin" element={<AdminPanel/>}/>
      <Route path="/admin/generatore-attestati" element={<GeneratoreAttestati/>}/>
      <Route path="/admin/approvazione-iscrizioni" element={<ApprovazioneIscrizioni/>}/>
      <Route path="/admin/delegazioni" element={<DelegazioniAdmin/>}/>
      <Route path="/delegation" element={<Delegation/>}/>
      <Route path="/delegation/espace-reserve" element={<DelegationEspace/>}/>
      {SHOW_PAID_SERVICES && <Route path="/host" element={<HostOnboarding/>}/>}
      <Route path="/manifesto" element={<Manifesto/>}/>
      {SHOW_PAID_SERVICES && <Route path="/host/dashboard" element={<HostDashboard/>}/>}
      <Route path="/account/bookings" element={<AccountBookings/>}/>
      <Route path="/booking/success" element={<BookingResult outcome="success"/>}/>
      <Route path="/booking/cancelled" element={<BookingResult outcome="cancelled"/>}/>
      <Route path="/richiedi-accesso" element={<RichiediAccesso/>}/>
      <Route path="/login" element={<LoginUnificato/>}/>
      <Route path="/accedi" element={<AccountLogin/>}/>
      <Route path="/account/forgot-password" element={<ForgotPassword/>}/>
      <Route path="/account/reset-password" element={<ResetPassword/>}/>
      {/* Citizen aliases (Wave Final) — backend invia /reset-password?token=... per cittadini */}
      <Route path="/reset-password" element={<ResetPassword/>}/>
      <Route path="/forgot-password" element={<ForgotPassword/>}/>
      <Route path="/suggerisci-evento" element={<SuggerisciEvento/>}/>
      <Route path="/come-funziona" element={<ComeFunziona/>}/>
      <Route path="/guida" element={<GuidaIndex/>}/>
      <Route path="/benvenuto" element={<GuidaBenvenuto/>}/>
      <Route path="/guida/benvenuto" element={<GuidaBenvenuto/>}/>
      <Route path="/storia-protocollo" element={<StoriaProtocollo/>}/>
      <Route path="/radici-del-progetto" element={<StoriaProtocollo/>}/>
      <Route path="/capsule-del-tempo" element={<CapsuleDelTempo/>}/>
      <Route path="/punti-merito" element={<PuntiMerito/>}/>
      <Route path="/sentinella-sismica" element={<SentinellaSismica/>}/>
      <Route path="/unlock/campovalano" element={<UnlockCampovalano/>}/>
      <Route path="/monumento/:id" element={<MonumentDetail/>}/>
      <Route path="/comune/:slug/luogo/:id" element={<MonumentDetail/>}/>
      <Route path="/agora" element={<Agora/>}/>
      <Route path="/stazione-meteo-emotiva" element={<StazioneMeteoEmotiva/>}/>
      <Route path="/emergenza" element={<Emergenza/>}/>
      <Route path="/cittadinanza-attiva" element={<CittadinanzaAttiva/>}/>
      <Route path="/passaporto-volontariato" element={<CittadinanzaAttiva/>}/>
      <Route path="/radici" element={<CittadinanzaAttiva/>}/>
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <AccountDashboard/>
          </ProtectedRoute>
        }
      />
      <Route path="/eventi" element={<EventiIndex/>}/>
      <Route path="/eventi/:eventId" element={<EventoDetail/>}/>
      <Route path="/weekend" element={<Weekend/>}/>
      <Route path="/prodotti" element={<Prodotti/>}/>
      <Route path="/prodotti/:id" element={<ProductDetail/>}/>
      <Route path="/mercato/:id" element={<ProductDetail/>}/>
      <Route path="/eccellenze" element={<EccellenzeIndex/>}/>
      <Route path="/eccellenze/:id" element={<EccellenzaDetail/>}/>
      <Route path="/cammini" element={<Cammini/>}/>
      <Route path="/cammini/:slug" element={<CamminoDetail/>}/>
      <Route path="/adotta" element={<Navigate to="/" replace/>}/>
      <Route path="/grazie" element={<Navigate to="/" replace/>}/>
      <Route path="/cookie-policy" element={<CookiePolicy/>}/>
      <Route path="/termini" element={<Termini/>}/>
      <Route path="/privacy" element={<Privacy/>}/>
      <Route path="/regole-piazza" element={<RegolePiazza/>}/>
      <Route path="/dati-societari" element={<Navigate to="/chi-siamo#dati-societari" replace/>}/>
      <Route path="/chi-siamo" element={<ChiSiamo/>}/>
      <Route path="/contatti" element={<Contatti/>}/>
      <Route
        path="/archivio/mosciano-sant-angelo/fondo-di-matteo"
        element={<Navigate to="/archivio/mosciano-sant-angelo/libro-tonino-di-matteo" replace/>}
      />
      <Route path="/archivio/:comuneSlug/:collSlug" element={<ArchiveCollectionView/>}/>
      <Route path="/registrati" element={<Registrati/>}/>
      <Route path="/collabora" element={<Collabora/>}/>
      <Route path="/lavora-con-noi" element={<Collabora/>}/>
      <Route path="/censisci-luogo" element={<CensisciLuogo/>}/>
      <Route path="/censisci" element={<CensisciLuogo/>}/>
      <Route path="/custode" element={<CreatorPassBorghi/>}/>
      <Route path="/ambasciatore" element={<Navigate to="/custode" replace/>}/>
      <Route path="/ambasciatori" element={<Ambasciatori/>}/>
      <Route path="/creator-pass" element={<CreatorPassBorghi/>}/>
      <Route path="/segnala-bene-comune" element={<CensisciLuogo/>}/>
      <Route path="/mappa-mondo" element={<Navigate to="/mappa-del-mondo" replace/>}/>
      <Route path="/mappa" element={<MappaTerritorio/>}/>
      <Route path="/mappa-interattiva" element={<MappaInterattiva/>}/>
      <Route path="/esplora-la-mappa" element={<EsploraLaMappa/>}/>
      <Route path="/mappa-globale" element={<MappaGlobaleRedirect/>}/>
      <Route path="/global" element={<Global/>}/>
      <Route path="/stampa" element={<Stampa/>}/>
      <Route path="/ufficio-stampa" element={<UfficioStampa/>}/>
      <Route path="/archivio-eventi" element={<ArchivioEventi/>}/>
      <Route path="/universita" element={<Universita/>}/>
      <Route path="/aderisci" element={<Aderisci/>}/>
      <Route path="/protocollo" element={<ProtocolloVetrina/>}/>
      <Route
        path="/protocollo/governance"
        element={
          <GovernanceGate>
            <GovernanceDashboardPage/>
          </GovernanceGate>
        }
      />
      <Route path="/sanita" element={<Sanita/>}/>
      <Route path="/registro-innovazione" element={<RegistroInnovazione/>}/>
      <Route
        path="/governance"
        element={
          <GovernanceGate>
            <GovernanceDashboardPage/>
          </GovernanceGate>
        }
      />
      <Route
        path="/stanza-governance"
        element={
          <GovernanceGate>
            <GovernanceDashboardPage/>
          </GovernanceGate>
        }
      />
      <Route path="/ecosistema-governance" element={<NetworkGovernance/>}/>
      <Route path="/network-governance" element={<NetworkGovernance/>}/>
      <Route path="/centrale-comando" element={<CentraleComando/>}/>
      <Route path="/hub-progetti" element={<CentraleComando/>}/>
      <Route path="/generazione-6amico" element={<Generazione6Amico/>}/>
      <Route path="/generazione" element={<Generazione6Amico/>}/>
      <Route path="/stanza-sindaci" element={<MayorsCouncilPage/>}/>
      <Route path="/mayors-council" element={<MayorsCouncilPage/>}/>
      <Route path="/embed/eventi" element={<EmbedFrame kind="eventi"/>}/>
      <Route path="/embed/vetrina" element={<EmbedFrame kind="vetrina"/>}/>
      <Route path="/embed/avvisi" element={<EmbedFrame kind="avvisi"/>}/>

      {/* Non intercettare /api/* (es. /api/health su yarn start → falso "Pagina non trovata") */}
      <Route path="/api/*" element={<ApiBackendRedirect/>}/>

      {/* Catch-all for QR-Code legacy URLs: /campli/battaglia-di-campli, /campli/casa-del-farmacista, etc.
          Must stay after every specific route; "*" below catches the rest. */}
      <Route path="/:village/:slug" element={<MonumentBySlug/>}/>
      <Route path="/origini" element={<Origini/>}/>
      <Route path="/per-comuni" element={<PerComuni/>}/>
      <Route path="*" element={<NotFound/>}/>
      </Routes>
      </Suspense>
    </>
  );
}

function AppShell() {
  const { pathname, search } = useLocation();
  const [searchParams] = useSearchParams();
  const { shellClass } = useThemeLayout();

  if (isMaintenanceMode && !isAdminAccessPath(pathname)) {
    return (
      <div className="App">
        <MaintenanceLanding />
      </div>
    );
  }

  const isStandaloneVideo = pathname === "/video";
  const isImprontaShell =
    pathname === "/impronta" || /^\/comune\/[^/]+\/impronta$/.test(pathname);
  const isAppShell = isAppPath(pathname);
  const isWebviewShell = pathname === "/app/webview";
  const isEmbedShell =
    isEmbedQuery(search) ||
    searchParams.get("embed") === "1" ||
    searchParams.get("webview") === "1";
  const isNodoZeroShell = pathname === "/campli" || pathname.startsWith("/campli/");
  const isAdminShell = isAdminAccessPath(pathname);
  const isImmersivaShell = pathname === "/piazze" || pathname.startsWith("/piazze-immersiva");
  const isEsploraMap =
    pathname === "/esplora-la-mappa" ||
    pathname.startsWith("/esplora-la-mappa/") ||
    pathname === "/mappa-globale";
  const isMinimalShell =
    isStandaloneVideo || isImprontaShell || isAppShell || isWebviewShell || isEmbedShell || isNodoZeroShell || isAdminShell || isImmersivaShell;
  const shellKind = isEmbedShell
    ? "embed"
    : isNodoZeroShell
      ? "nodo-zero"
    : isImmersivaShell
      ? "immersiva"
    : isAppShell || isWebviewShell
      ? "app"
      : isImprontaShell
        ? "impronta"
        : "";
  const appClass = shellKind
    ? `App App--${shellKind}`
    : isAdminShell
      ? "App glow-tech-page glow-dashboard min-h-screen"
      : `App ${shellClass}`;
  return (
    <div className={isMinimalShell ? appClass : `App ${shellClass}`}>
      <GoogleSiteTranslator />
      {!isMinimalShell && <Header/>}
      <main className={isMinimalShell || isEsploraMap ? "" : "pb-4"}>
        <AppRouter/>
      </main>
      {!isMinimalShell && !isEsploraMap && <Footer/>}
      {!isMinimalShell && <AmbasciatoreFab/>}
      {!isMinimalShell && <ChatWidget/>}
      {!isMinimalShell && <ChatLauncher/>}
      {!isMinimalShell && <MapQuickAccess/>}
    </div>
  );
}

function AppErrorBoundaryShell({ children }) {
  const { pathname } = useLocation();
  return <AppErrorBoundary resetKey={pathname}>{children}</AppErrorBoundary>;
}

function App() {
  return (
    <LangProvider>
      <AuthProvider>
        <SupabaseAuthProvider>
          <LangSupabaseSync />
          <BrowserRouter>
            <AppErrorBoundaryShell>
              <CiceroneProvider>
                <InterMappaProvider>
                  <ThemeLayoutProvider>
                    <ScrollToHash/>
                    <AnalyticsRouteListener/>
                    <LaunchSeoMeta/>
                    <AppShell/>
                  </ThemeLayoutProvider>
                </InterMappaProvider>
              </CiceroneProvider>
            </AppErrorBoundaryShell>
          </BrowserRouter>
        </SupabaseAuthProvider>
      </AuthProvider>
    </LangProvider>
  );
}

export default App;
